import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { API_BASE } from './index';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import usePersistedState, { clearPersistedKeys } from './hooks/usePersistedState';
import CartLineItem from './CartLineItem';
import CartDrawer from './CartDrawer';

function StripeCheckoutForm({ orderTotal, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href
            },
            redirect: 'if_required'
        });
        console.log('Stripe result:', JSON.stringify(result));
        const { error, paymentIntent } = result;
        if (error) {
            setSubmitting(false);
            onError(error.message);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            onSuccess();
        } else {
            setSubmitting(false);
            onError('Payment could not be confirmed. Please try again.');
        }
    } catch (err) {
        setSubmitting(false);
        onError(err.message);
    }
};

    return (
        <div>
            <PaymentElement />
            <button
                onClick={handleSubmit}
                disabled={!stripe || submitting}
                className="btn-stripe-submit">
                {submitting ? 'Processing...' : `Pay $${orderTotal.toFixed(2)}`}
            </button>
        </div>
    );
}

function AddressAutocomplete({ value, onChange, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(() => {
      fetch(`${API_BASE}/api/autocomplete?input=${encodeURIComponent(val)}`)
        .then(r => r.json())
        .then(data => { setSuggestions(data); setShowDropdown(true); })
        .catch(() => setSuggestions([]));
    }, 300);
  };

  const handleSelect = (suggestion) => {
    onChange(suggestion);
    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <div className="address-wrap">
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        placeholder={placeholder}
        required
        className="form-input"
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="address-dropdown">
          {suggestions.map((s, i) => (
            <li key={i}
              onMouseDown={() => handleSelect(s)}
              className="address-dropdown__item">
              📍 {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactUsLink({ onClick }) {
  return (
    <button onClick={onClick} className="contact-link-btn">contact us</button>
  );
}

const PLACEHOLDER_IMAGE_URL = "https://storage.googleapis.com/rugelach-images/placeholder.svg";

function hasProductImage(product) {
  return Boolean(product.imageUrl) && product.imageUrl !== "https://googleapis.com";
}

function NoImagePlaceholder({ enhanced }) {
  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      backgroundColor: "#FFF0ED",
    }}>
      <img src={PLACEHOLDER_IMAGE_URL} alt="Coming soon" style={{ width: "60%", height: "auto" }} />
      {enhanced && (
        <span className="welcome-popup__surprise-badge">✨ Special surprise inside...</span>
      )}
    </div>
  );
}

function WelcomeFireworks() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const colors = ['#D4A853', '#C0392B', '#2C1A14', '#FFFDF9'];
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.parentElement.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const BURST_COUNT = 7;
    const PARTICLES_PER_BURST = 16;
    const BURST_DURATION = 2000;
    const LOOP_COUNT = 3;

    let rafId = null;
    let running = true;
    let loopsDone = 0;
    let bursts = [];
    let startTime = 0;

    const makeBurst = (delay) => {
      const x = width * (0.2 + Math.random() * 0.6);
      const y = height * (0.25 + Math.random() * 0.35);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const particles = [];
      for (let i = 0; i < PARTICLES_PER_BURST; i++) {
        const angle = (Math.PI * 2 * i) / PARTICLES_PER_BURST + Math.random() * 0.3;
        const speed = 0.05 + Math.random() * 0.05;
        particles.push({ angle, speed });
      }
      return { x, y, color, particles, delay };
    };

    const startLoop = () => {
      bursts = Array.from({ length: BURST_COUNT }, (_, i) => makeBurst(i * 150));
      startTime = performance.now();
    };

    startLoop();

    const draw = (now) => {
      if (!running) return;
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, width, height);

      let allDone = true;
      bursts.forEach(burst => {
        const t = elapsed - burst.delay;
        if (t < 0) { allDone = false; return; }
        if (t > BURST_DURATION) return;
        allDone = false;
        const alpha = Math.max(1 - t / BURST_DURATION, 0);
        burst.particles.forEach(p => {
          const dist = p.speed * t;
          const gravity = 0.00004 * t * t;
          const px = burst.x + Math.cos(p.angle) * dist;
          const py = burst.y + Math.sin(p.angle) * dist + gravity;
          ctx.beginPath();
          ctx.arc(px, py, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = burst.color;
          ctx.globalAlpha = alpha;
          ctx.fill();
        });
      });
      ctx.globalAlpha = 1;

      if (allDone) {
        loopsDone += 1;
        if (loopsDone >= LOOP_COUNT) {
          running = false;
          ctx.clearRect(0, 0, width, height);
          return;
        }
        startLoop();
      }
      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);

    return () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} className="welcome-popup__canvas" />;
}

const CART_TTL_MS = 24 * 60 * 60 * 1000;

export default function Storefront({ uiMode, setUiMode, products, loading, loadError }) {
  const [cart, setCart] = usePersistedState('rugalachDad_cart', [], { storage: 'local', ttlMs: CART_TTL_MS });
  const [cartRestoreNotice, setCartRestoreNotice] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showStickyNav, setShowStickyNav] = useState(true);
  const [fulfillmentType, setFulfillmentType] = usePersistedState('rugalachDad_fulfillmentType', null, { storage: 'session' });
  const [paymentMethod, setPaymentMethod] = useState(null); // 'CARD' | 'CASH' | 'VENMO' | 'COUPON'
  const [paymentStep, setPaymentStep] = useState('select'); // 'select' | 'processing' | 'paid' | 'error'
  const [paymentError, setPaymentError] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [couponCode, setCouponCode] = usePersistedState('rugalachDad_couponCode', '', { storage: 'session' });
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [surpriseGift, setSurpriseGift] = useState(null);
  const [address, setAddress] = usePersistedState('rugalachDad_address', '', { storage: 'session' });
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [fulfillmentLocked, setFulfillmentLocked] = useState(() => fulfillmentType != null);
  const [customizeProduct, setCustomizeProduct] = useState(null);
  const [customizations, setCustomizations] = useState({ productCustomizations: [], toppings: [] });
  const [selectedSize, setSelectedSize] = useState('SMALL');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = usePersistedState('rugalachDad_selectedDate', null, { storage: 'session' });
  const [capacityError, setCapacityError] = useState(null);
  const [addToCartError, setAddToCartError] = useState(null);
  const [checkingCapacity, setCheckingCapacity] = useState(false);
  const [customerInfo, setCustomerInfo] = usePersistedState('rugalachDad_customerInfo', { name: '', email: '', phone: '', comments: '' }, { storage: 'session' });
  const [newsletterOptIn, setNewsletterOptIn] = usePersistedState('rugalachDad_newsletterOptIn', true, { storage: 'session' });
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [hoveredAddonId, setHoveredAddonId] = useState(null);
  const [cartToast, setCartToast] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('All');
  const [welcomeCampaignEnabled, setWelcomeCampaignEnabled] = useState(false);
  const [standbyModeEnabled, setStandbyModeEnabled] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [dontShowWelcomeAgain, setDontShowWelcomeAgain] = useState(false);
  const welcomeTimeoutRef = useRef(null);

  // Product categories for filtering and grouping
  const PRODUCT_CATEGORIES = ['Rugalach', 'Cookies', 'Specials'];
  const [shippingFeeAmount, setShippingFeeAmount] = useState(0);
  const [venmoHandle, setVenmoHandle] = useState('@Asaf-Shechter');
  const [localDeliveryMinimum, setLocalDeliveryMinimum] = useState(25);
  const localDeliveryDisabled = localDeliveryMinimum < 0;
  const shippingDisabled = shippingFeeAmount < 0;
  const shippingFee = fulfillmentType === 'shipping' ? shippingFeeAmount : 0.00;
  const totalCartValue = cart.reduce((s, i) => s + i.lineTotal, 0);
  const orderTotal = Math.round((totalCartValue + shippingFee) * 100) / 100;
  const finalOrderTotal = Math.max(0, Math.round((orderTotal - couponDiscount) * 100) / 100);
  const belowMinimumForDelivery = fulfillmentType === 'delivery' && (localDeliveryDisabled || totalCartValue < localDeliveryMinimum);
  
  const [sizeConfig, setSizeConfig] = useState({
    SMALL: { label: 'Small', step: 5, batchSize: Infinity },
    LARGE: { label: 'Large', step: 5, batchSize: Infinity },
    BALL: { label: 'Ball', step: 1, batchSize: Infinity },
  });
  const [selectedQuantity, setSelectedQuantity] = useState(sizeConfig.SMALL.step);
  const [batchCapacity, setBatchCapacity] = useState(Infinity);
  const [itemSizeOverrides, setItemSizeOverrides] = useState({});
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
	  fetch(`${API_BASE}/api/size-config`)
		.then(r => r.json())
		.then(data => {
		  setSizeConfig(prev => {
			const merged = {};
			for (const size of Object.keys(prev)) {
			  merged[size] = {
				label: prev[size].label,
				step: data[size]?.step ?? prev[size].step,
				batchSize: data[size]?.batchSize ?? prev[size].batchSize,
			  };
			}
			return merged;
		  });
		  setBatchCapacity(data.batchCapacity ?? Infinity);
		  setShippingFeeAmount(parseFloat(data.shippingFee) || 0);
		  if (data.venmoHandle) setVenmoHandle(data.venmoHandle);
		  setLocalDeliveryMinimum(() => {
			const parsed = parseFloat(data.localDeliveryMinimum);
			return Number.isNaN(parsed) ? 25 : parsed;
		  });
		  setItemSizeOverrides(data.itemOverrides ?? {});
		  setWelcomeCampaignEnabled(data.welcomeCampaignEnabled === true);
		  setStandbyModeEnabled(data.standbyModeEnabled === true);
		  if (data.welcomeCampaignEnabled === true && localStorage.getItem('rugalachDad_welcomeSeen') !== 'true') {
			  welcomeTimeoutRef.current = setTimeout(() => setShowWelcomePopup(true), 1500);
		  }
		})
		.catch(() => { /* keep fallback defaults */ })
		.finally(() => setConfigReady(true));
	  return () => {
		  if (welcomeTimeoutRef.current) clearTimeout(welcomeTimeoutRef.current);
	  };
	}, []);

  useEffect(() => {
    const handleScroll = () => setShowStickyNav(true);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (products.length === 0 || uiMode !== 'storefront') return;
    const newItems = products.filter(p => p.isNew);
    if(newItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % newItems.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [products, uiMode]);

  const goToPrevSlide = () => {
    const heroItems = products.filter(p => p.isNew);
    if (heroItems.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + heroItems.length) % heroItems.length);
  };

  const goToNextSlide = () => {
    const heroItems = products.filter(p => p.isNew);
    if (heroItems.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % heroItems.length);
  };

  useEffect(() => {
    if (uiMode === 'date-picker' && selectedDate) {
      checkCapacityForDate(selectedDate);
    }
  }, [cart]);

  // Always (re)load the pickup-date window when the picker is shown. Previously dates
  // were only fetched as a side effect of opening a product modal, so reaching the
  // picker without opening one — a cart restored from storage, or navigating straight
  // to checkout — left the grid empty. Refetching here also keeps availability current
  // with the latest cart contents.
  useEffect(() => {
    if (uiMode === 'date-picker') {
      fetchAvailableDates();
    }
  }, [uiMode]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, [uiMode]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (res.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (e) { alert('Error: ' + e.message); }
    setContactSubmitting(false);
  };

  const closeContact = () => {
    setShowContact(false);
    setContactSuccess(false);
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const openContactFromWelcome = () => {
    setShowWelcomePopup(false);
    setShowContact(true);
  };

  const dismissWelcomePopup = () => {
    if (dontShowWelcomeAgain) {
      localStorage.setItem('rugalachDad_welcomeSeen', 'true');
    }
    setShowWelcomePopup(false);
  };

  const totalItems = cart.length;

  const scrollToMenu = () => {
    if (uiMode !== 'storefront') setUiMode('storefront');
    setTimeout(() => {
      const el = document.getElementById('menu-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const checkCapacityForDate = async (date) => {
    if (!date) return;
    setCheckingCapacity(true);
    setCapacityError(null);
    try {
      const res = await fetch(`${API_BASE}/api/orders/check-capacity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pickupDate: date,
          items: cart.map(i => ({ size: i.size, quantity: i.quantity }))
        })
      });
      const data = await res.json();
      if (data.available !== true) {
        setCapacityError(data);
      }
    } catch {
      // fail silently — don't block the user if the check fails
    }
    setCheckingCapacity(false);
  };

  const tileBg = (d, isSelected) => {
    if (isSelected) return '#000000';
    const used = d.batchesUsed ?? 0;
    const capacity = d.capacity ?? 1;
    const pct = Math.min(used / capacity, 1);
    if (pct <= 0) return '#ffffff';
    if (pct >= 1) return '#D3D1C7';
    const stop = Math.round(pct * 100);
    return `linear-gradient(to top right, #D3D1C7 ${stop}%, #ffffff ${stop}%)`;
  };

  const handleOrderNowClick = () => {
    if (totalItems === 0) {
      scrollToMenu();
      return;
    }
    if (fulfillmentLocked) {
      // Route to the furthest incomplete step in the checkout flow
      if (!selectedDate) {
        setUiMode('date-picker');
      } else if (!customerInfo.name || !customerInfo.email) {
        setUiMode('customer-info');
      } else {
        setUiMode('review');
      }
      return;
    }
    setUiMode('order');
    setValidationResult(null);
  };

  const isUSAddress = (addr) => {
    const upper = addr.trim().toUpperCase();
    return upper.endsWith(', USA') || upper.endsWith(', US');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setValidationResult(null);
    if (!isUSAddress(address)) {
      setValidationResult({ valid: false, message: 'Sorry, we currently only serve addresses within the United States.' });
      return;
    }
    setValidating(true);
    fetch(`${API_BASE}/api/validate-delivery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address })
    })
      .then((res) => res.json())
      .then((data) => {
        setValidating(false);
        setValidationResult(data);
        setUiMode('choose-fulfillment');
      })
      .catch(() => {
        setValidating(false);
        setValidationResult({ valid: false, message: 'Could not validate address. Please try again.' });
      });
  };

  const handleFulfillmentChoice = (type) => {
    setFulfillmentType(type);
    setFulfillmentLocked(true);
    setUiMode('date-picker');
  };

  const clearFulfillmentLock = () => {
    setFulfillmentLocked(false);
    setFulfillmentType(null);
    setAddress('');
    setValidationResult(null);
    setCapacityError(null);
    setUiMode('order');
  };

  // unitPrice(size) = product.price / batchSize(size) — product.price is the cost of one oven batch
  const calcUnitPrice = (product, size) => {
    const activeSizes = getActiveSizeConfig(product);
    const sizeBatchSize = activeSizes[size]?.batchSize;
    if (!sizeBatchSize) return null; // size not configured for this product — caller must handle
    return Math.round((product.price / sizeBatchSize) * 100) / 100;
  };

  // "From" price shown on cards: the minimum quantity (step) of the cheapest-per-piece
  // size (the one with the largest batchSize) times that size's unit price.
  const calcFromPriceDetails = (product) => {
    const activeSizes = getActiveSizeConfig(product);
    const available = Object.entries(activeSizes).filter(([, cfg]) => Number.isFinite(cfg.batchSize) && cfg.batchSize > 0);
    if (available.length === 0) return null;
    const [cheapestSize, cheapestCfg] = available.reduce((best, entry) =>
      entry[1].batchSize > best[1].batchSize ? entry : best
    );
    const unit = calcUnitPrice(product, cheapestSize);
    if (unit == null) return null;
    const price = Math.round(unit * cheapestCfg.step * 100) / 100;
    return { price, step: cheapestCfg.step, label: cheapestCfg.label ?? cheapestSize };
  };

  const formatFromPrice = (product) => {
    const details = calcFromPriceDetails(product);
    if (details == null) return null;
    return `from $${details.price.toFixed(2)} / ${details.step}pc ${details.label}`;
  };

  const getActiveSizeConfig = (product) => {
    if (!product) return sizeConfig;
    const overrides = itemSizeOverrides[product.id];
    if (!overrides) return sizeConfig;
    const merged = {};
    for (const [size, cfg] of Object.entries(overrides)) {
      merged[size] = {
        label: sizeConfig[size]?.label ?? size,
        batchSize: cfg.batchSize,
        step: cfg.step,
      };
    }
    return merged;
  };

  // Re-validates a cart restored from localStorage (and a date restored from
  // sessionStorage) once real product/pricing/availability data has loaded, since the
  // persisted unitPrice/lineTotal/selectedDate are just stale snapshots.
  const cartHydratedRef = useRef(false);
  useEffect(() => {
    if (cartHydratedRef.current) return;
    if (!configReady || !products || products.length === 0) return;
    cartHydratedRef.current = true;
    if (cart.length === 0 && !selectedDate) return;

    let cancelled = false;
    (async () => {
      let finalCart = cart;

      if (cart.length > 0) {
        const productIds = [...new Set(cart.map(item => item.itemId))];
        const addonPricesByProduct = {};
        await Promise.all(productIds.map(async (id) => {
          try {
            const res = await fetch(`${API_BASE}/api/customizations?itemId=${id}`);
            const data = await res.json();
            const all = [...(data?.productCustomizations || []), ...(data?.toppings || [])];
            addonPricesByProduct[id] = new Map(all.map(a => [a.name, a.priceDelta]));
          } catch (e) {
            addonPricesByProduct[id] = new Map();
          }
        }));
        if (cancelled) return;

        let removedCount = 0;
        let pricesChanged = false;
        const revalidated = [];
        for (const item of cart) {
          const product = products.find(p => p.id === item.itemId);
          const unit = product ? calcUnitPrice(product, item.size) : null;
          const addonMap = addonPricesByProduct[item.itemId] || new Map();
          const names = item.customizations || [];
          const hasUnknownAddon = names.some(name => !addonMap.has(name));
          if (!product || unit == null || hasUnknownAddon) {
            removedCount++;
            continue;
          }
          const addonTotal = names.reduce((sum, name) => sum + (addonMap.get(name) || 0), 0);
          const unitPrice = Math.round((unit + addonTotal) * 100) / 100;
          const lineTotal = Math.round(unitPrice * item.quantity * 100) / 100;
          if (unitPrice !== item.unitPrice || lineTotal !== item.lineTotal) pricesChanged = true;
          revalidated.push({ ...item, unitPrice, lineTotal });
        }

        finalCart = revalidated;
        if (removedCount > 0 || pricesChanged) {
          setCart(revalidated);
          setCartRestoreNotice(
            removedCount > 0
              ? `${removedCount} item${removedCount > 1 ? 's' : ''} from your saved cart ${removedCount > 1 ? 'are' : 'is'} no longer available and ${removedCount > 1 ? 'were' : 'was'} removed.`
              : 'Prices in your saved cart were refreshed to reflect current pricing.'
          );
          setTimeout(() => setCartRestoreNotice(null), 6000);
        }
      }

      if (!cancelled && selectedDate) {
        try {
          const res = await fetch(`${API_BASE}/api/orders/check-capacity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pickupDate: selectedDate,
              items: finalCart.map(i => ({ size: i.size, quantity: i.quantity }))
            })
          });
          const data = await res.json();
          if (!cancelled && data.available !== true) {
            setSelectedDate(null);
          }
        } catch (e) {
          // fail silently — don't block the user if the check fails, same as checkCapacityForDate
        }
      }
    })();

    return () => { cancelled = true; };
  }, [configReady, products]);

  // Builds the selectable pickup-date window client-side as a last resort so the
  // picker is never empty. Mirrors the backend rule: normally starts tomorrow, but
  // after 2:00 PM Eastern the earliest date shifts to the day after tomorrow.
  const buildFallbackDates = () => {
    const easternNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const startOffset = easternNow.getHours() >= 14 ? 2 : 1;
    const base = new Date(easternNow.getFullYear(), easternNow.getMonth(), easternNow.getDate());
    const dates = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + startOffset + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dates.push({ date: iso, available: true, batchesUsed: 0, capacity: Infinity });
    }
    return dates;
  };

  // Loads the pickup-date window from the backend. Any failure — network error, non-2xx,
  // or an empty/invalid payload — falls back to a locally-generated window so the date
  // picker always shows dates. The real per-date capacity is re-checked on selection and
  // at order placement, so an optimistic fallback date can't overbook.
  const fetchAvailableDates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/available-dates?batches=${getTotalBatchesUsed()}`);
      if (!res.ok) throw new Error(`available-dates responded ${res.status}`);
      const data = await res.json();
      setAvailableDates(Array.isArray(data) && data.length > 0 ? data : buildFallbackDates());
    } catch (e) {
      setAvailableDates(buildFallbackDates());
    }
  };

  const openCustomizeModal = async (product) => {
    setCustomizeProduct(product);
    const activeSizes = getActiveSizeConfig(product);
	const firstSize = Object.keys(activeSizes)[0];
	setSelectedSize(firstSize);
	setSelectedQuantity(activeSizes[firstSize].step);
	setAddToCartError(null);
    setSelectedAddons([]);
    setCapacityError(null);
    try {
      const res = await fetch(`${API_BASE}/api/customizations?itemId=${product.id}`);
      const data = await res.json();
      setCustomizations(data || { productCustomizations: [], toppings: [] });
    } catch (e) { setCustomizations({ productCustomizations: [], toppings: [] }); }
    await fetchAvailableDates();
  };

  const closeModal = () => {
    setCustomizeProduct(null);
    setCapacityError(null);
	setAddToCartError(null);
  };

  const toggleAddon = (addon, isTopping = false) => {
    setSelectedAddons(prev => {
      if (isTopping) {
        const alreadySelected = prev.find(a => a.id === addon.id);
        if (alreadySelected) return prev.filter(a => a.id !== addon.id);
        const withoutToppings = prev.filter(a => !customizations.toppings.find(t => t.id === a.id));
        return [...withoutToppings, addon];
      }
      return prev.find(a => a.id === addon.id) ? prev.filter(a => a.id !== addon.id) : [...prev, addon];
    });
  };

  const calcLineTotal = () => {
    if (!customizeProduct) return 0;
    const unit = calcUnitPrice(customizeProduct, selectedSize);
    if (unit == null) return 0;
    const addonTotal = selectedAddons.reduce((s, a) => s + a.priceDelta, 0);
    const unitPrice = Math.round((unit + addonTotal) * 100) / 100;
    return Math.round(unitPrice * selectedQuantity * 100) / 100;
  };
  
  const getCartQuantityForSize = (size) => {
    const cartQty = cart.filter(i => i.size === size).reduce((sum, i) => sum + i.quantity, 0);
    // Include quantity already dialed up in the open modal (not yet added to cart)
    const modalQty = (customizeProduct && selectedSize === size) ? selectedQuantity : 0;
    return cartQty + modalQty;
  };
  
  const getTotalBatchesUsed = (overrides = {}) => {
    let total = 0;
    for (const size of Object.keys(sizeConfig)) {
      const qty = overrides[size] !== undefined
        ? overrides[size]
        : getCartQuantityForSize(size);
      total += qty / sizeConfig[size].batchSize;
    }
    return total;
  };

  // Standby Mode assumes a max capacity of 1 batch/day (mirrors the backend's
  // weekend-capacity rule). The cart doesn't know which date will be picked yet,
  // so this is applied globally to any capacity check while Standby Mode is on.
  const effectiveCapacity = standbyModeEnabled ? 1 : batchCapacity;

  // Would adding `extraQty` of `size` push total batches over capacity?
  const wouldExceedCapacity = (size, extraQty) => {
	  const currentQty = getCartQuantityForSize(size);
	  const projected = getTotalBatchesUsed({ [size]: currentQty + extraQty });
	  return (Math.round(projected * 10000) / 10000) > effectiveCapacity;
	};

  // True if cart is currently at/over capacity (any size's "+" should be disabled)
  const isCartAtCapacity = () => {
	  const currentTotal = getTotalBatchesUsed();
	  return currentTotal >= effectiveCapacity;
	};

  const addToCartFromModal = () => {
	const cartItemId = `${customizeProduct.id}-${selectedSize}-${selectedAddons.map(a => a.name).sort().join(',')}`;
	if (wouldExceedCapacity(selectedSize, 0)) {
		setAddToCartError(
		'Sorry, we have reached the maximum we can make in a day. ' +
		'If you need more than this amount, please contact us and we will review and get back to you.'
		);
		return;
	}
	setAddToCartError(null);

    const unit = calcUnitPrice(customizeProduct, selectedSize);
    if (unit == null) {
      setAddToCartError('This size is currently unavailable for this product.');
      return;
    }
    const addonTotal = selectedAddons.reduce((s, a) => s + a.priceDelta, 0);
    const unitPrice = Math.round((unit + addonTotal) * 100) / 100;
    const lineTotal = Math.round(unitPrice * selectedQuantity * 100) / 100;
    const customizationString = selectedAddons.map(a => a.name).sort().join(',');
    const cartItem = {
      id: cartItemId,
      itemId: customizeProduct.id,
      itemName: customizeProduct.name,
      size: selectedSize,
      quantity: selectedQuantity,
      unitPrice,
      lineTotal,
      customizations: selectedAddons.map(a => a.name),
    };
    setCart(prev => {
      const existingItemIndex = prev.findIndex(item => item.id === cartItemId);
      if (existingItemIndex > -1) {
        const newCart = [...prev];
        const existingItem = newCart[existingItemIndex];
        newCart[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + selectedQuantity,
          lineTotal: Math.round((existingItem.lineTotal + lineTotal) * 100) / 100
        };
        return newCart;
      }
      return [...prev, cartItem];
    });
    setCartToast({ quantity: selectedQuantity, size: selectedSize, name: customizeProduct.name });
    setTimeout(() => setCartToast(null), 2500);
    closeModal();
  };

  const updateCartItemQuantity = (cartItemId, newQuantity) => {
    setCart(prev => {
      const item = prev.find(i => i.id === cartItemId);
      if (!item) return prev;

      const cfg = sizeConfig[item.size];
      if (newQuantity < cfg.step) {
        // Drop below minimum -> remove the line entirely
        const next = prev.filter(i => i.id !== cartItemId);
        if (next.length === 0) {
          // Cart now empty -> bounce back to menu
          setFulfillmentLocked(false);
          setFulfillmentType(null);
          setAddress('');
          setValidationResult(null);
          setCapacityError(null);
          setSelectedDate(null);
          setCouponCode('');
          setCouponApplied('');
          setCouponDiscount(0);
          setCouponError('');
          setUiMode('storefront');
        }
        return next;
      }

      return prev.map(i => {
        if (i.id !== cartItemId) return i;
        const newLineTotal = Math.round(i.unitPrice * newQuantity * 100) / 100;
        return { ...i, quantity: newQuantity, lineTotal: newLineTotal };
      });
    });
  };

  const incrementCartItem = (cartItemId) => {
    const item = cart.find(i => i.id === cartItemId);
    if (!item) return;
    const step = sizeConfig[item.size].step;
	if (wouldExceedCapacity(item.size, step)) {
		return; // already at cap; button should also be disabled (see 2.8)
	}
    updateCartItemQuantity(cartItemId, item.quantity + step);
  };

  const decrementCartItem = (cartItemId) => {
    const item = cart.find(i => i.id === cartItemId);
    if (!item) return;
    const step = sizeConfig[item.size].step;
    updateCartItemQuantity(cartItemId, item.quantity - step);
  };

  const removeCartItem = (cartItemId) => {
    setCart(prev => {
      const next = prev.filter(i => i.id !== cartItemId);
      if (next.length === 0) {
        setFulfillmentLocked(false);
        setFulfillmentType(null);
        setAddress('');
        setValidationResult(null);
        setCapacityError(null);
        setSelectedDate(null);
        setCouponCode('');
        setCouponApplied('');
        setCouponDiscount(0);
        setCouponError('');
        setUiMode('storefront');
      }
      return next;
    });
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await fetch(`${API_BASE}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode,
          orderSubtotal: totalCartValue,
          customerEmail: customerInfo.email
        })
      });
      const data = await res.json();
      if (data.valid) {
        setCouponApplied(couponCode.toUpperCase());
        setCouponDiscount(data.discountAmount);
        setCouponError('');
      } else {
        setCouponApplied('');
        setCouponDiscount(0);
        setCouponError(data.errorMessage || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponApplied('');
      setCouponDiscount(0);
      setCouponError('Unable to validate coupon. Please try again.');
    }
    setCouponLoading(false);
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied('');
    setCouponDiscount(0);
    setCouponError('');
  };

  const handlePlaceOrder = async () => {
    try {
      setOrderSubmitting(true);

      // Step 1: Create the order in PENDING_PAYMENT state
      const res = await fetch(`${API_BASE}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: customerInfo.name,
          customerEmail: customerInfo.email,
          customerPhone: customerInfo.phone,
          deliveryAddress: address,
          fulfillmentType: fulfillmentType || 'pickup',
          pickupDate: selectedDate,
          totalAmount: orderTotal,
          items: cart.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            size: item.size,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
            customizations: item.customizations || []
          })),
          comments: customerInfo.comments,
          ...(couponApplied ? { couponCode: couponApplied } : {})
        })
      });

      if (!res.ok) {
        const err = await res.json();
        alert('Error placing order: ' + (err.error || 'Unknown error'));
        setOrderSubmitting(false);
        return;
      }

      const data = await res.json();
      const newOrderId = data.id;
      setCreatedOrderId(newOrderId);

      // Step 2: Coupon fully covered the order — skip Stripe and go straight to confirmation
      if (couponApplied && data.totalAmount === 0) {
        setPaymentMethod('COUPON');
        setPaymentStep('paid');
        setUiMode('order-success');
        setOrderSubmitting(false);
        return;
      }

      // Step 2a: Sync coupon/discount state to the server-authoritative values on the
      // created order — covers both a manually-applied coupon and an auto-applied
      // FRESH5_<itemId> surprise-gift discount the customer never explicitly requested.
      if (data.couponCode && data.discountAmount > 0) {
        setCouponApplied(data.couponCode);
        setCouponDiscount(data.discountAmount);
        if (data.couponCode.startsWith('FRESH5_')) {
          const surpriseItemId = Number(data.couponCode.slice('FRESH5_'.length));
          const surpriseProduct = products.find(p => p.id === surpriseItemId);
          setSurpriseGift({
            productName: surpriseProduct ? surpriseProduct.name : 'a surprise item',
            discountAmount: data.discountAmount,
          });
        }
      }

      // Step 2b: Transition to payment step
      setUiMode('payment');
      setPaymentStep('select');
      setOrderSubmitting(false);
    } catch (error) {
      alert('Error: ' + error.message);
      setOrderSubmitting(false);
    }
  };
  
  const handleCashPayment = async () => {
    try {
      setPaymentStep('processing');
      setPaymentError(null);

      const res = await fetch(`${API_BASE}/api/orders/${createdOrderId}/cash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        const err = await res.json();
        setPaymentError(err.error || 'Failed to submit cash payment');
        setPaymentStep('error');
        return;
      }

      setPaymentStep('paid');
      setUiMode('order-success');
    } catch (error) {
      setPaymentError(error.message);
      setPaymentStep('error');
    }
  };

  const handleVenmoPayment = async () => {
    try {
      setPaymentStep('processing');
      setPaymentError(null);

      const res = await fetch(`${API_BASE}/api/orders/${createdOrderId}/venmo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
		  let message = 'Failed to submit Venmo payment';
		  try {
			const err = await res.json();
			message = err.error || message;
		  } catch {
			// response body wasn't JSON (e.g. a 403/500 with no body) — fall back to a generic message
		  }
		  setPaymentError(message);
		  setPaymentStep('error');
		  return;
		}

      setPaymentStep('paid');
      setUiMode('order-success');
    } catch (error) {
      setPaymentError(error.message);
      setPaymentStep('error');
    }
  };

  const initStripePayment = async () => {
    try {
        setPaymentStep('processing');
        setPaymentError(null);

        const res = await fetch(`${API_BASE}/api/orders/${createdOrderId}/stripe/create-intent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (!res.ok) {
            setPaymentError(data.error || 'Failed to initialize payment');
            setPaymentStep('error');
            return;
        }

        const stripe = await loadStripe(data.publishableKey);
        setStripePromise(Promise.resolve(stripe));
        setStripeClientSecret(data.clientSecret);
        setPaymentStep('select');
    } catch (err) {
        setPaymentError(err.message);
        setPaymentStep('error');
    }
  };

  const resetOrder = () => {
    setUiMode('storefront');
    setCart([]);
    setFulfillmentLocked(false);
    setFulfillmentType(null);
    setAddress('');
    setCustomerInfo({ name: '', email: '', phone: '', comments: '' });
    setNewsletterOptIn(true);
    setSelectedDate(null);
    setPaymentMethod(null);
    setPaymentStep('select');
    setPaymentError(null);
    setCreatedOrderId(null);
    setCouponCode('');
    setCouponApplied('');
    setCouponDiscount(0);
    setCouponError('');
    setSurpriseGift(null);
    clearPersistedKeys(['rugalachDad_cart'], 'local');
    clearPersistedKeys([
      'rugalachDad_fulfillmentType',
      'rugalachDad_address',
      'rugalachDad_customerInfo',
      'rugalachDad_newsletterOptIn',
      'rugalachDad_selectedDate',
      'rugalachDad_couponCode',
    ], 'session');
  };

  return (
    <div className="storefront-root">
      <>
      <Helmet defer={false}>
        <title>{
          uiMode === 'about' ? 'About Us — Rugalach Dad' :
          uiMode === 'order' || uiMode === 'choose-fulfillment' || uiMode === 'date-picker' || uiMode === 'customer-info' || uiMode === 'review' || uiMode === 'payment' ? 'Order — Rugalach Dad' :
          uiMode === 'order-success' ? 'Order Confirmed — Rugalach Dad' :
          'Rugalach Dad — Handmade Rugelach in Alpharetta, GA'
        }</title>
      </Helmet>
      {configReady && products && products.length > 0 && (
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Bakery",
              "@id": "https://rugalachdad.com/#bakery",
              "name": "Rugalach Dad",
              "url": "https://rugalachdad.com",
              "description": "Handmade rugelach and baked goods. Local pickup in Alpharetta GA, delivery in Forsyth County, or shipped across the US.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Alpharetta",
                "addressRegion": "GA",
                "addressCountry": "US"
              },
              "areaServed": [
                { "@type": "City", "name": "Alpharetta" },
                { "@type": "AdministrativeArea", "name": "Forsyth County, GA" },
                { "@type": "Country", "name": "US" }
              ],
              "priceRange": "$$",
              "servesCuisine": "Bakery",
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Menu",
                "itemListElement": products.map(product => {
                  const activeCfg = getActiveSizeConfig(product);
                  const firstSize = Object.keys(activeCfg)[0];
                  const smallCfg = activeCfg['SMALL'] || activeCfg[firstSize];
                  const globalSmallBatchSize = sizeConfig['SMALL']?.batchSize || 40;
                  const smallUnitPrice = product.price / 10;
                  const batchValue = smallUnitPrice * globalSmallBatchSize;
                  const unitPrice = batchValue / smallCfg.batchSize;
                  const minOrderPrice = unitPrice * smallCfg.step;
                  return {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Product",
                      "name": product.name,
                      "description": product.description || product.name,
                      "category": product.category || "Baked Goods",
                      "image": product.imageUrl || "https://storage.googleapis.com/rugelach-images/branding/logo-icon.png",
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "USD",
                        "price": minOrderPrice.toFixed(2),
                        "availability": "https://schema.org/InStock",
                        "url": "https://rugalachdad.com/"
                      }
                    }
                  };
                })
              },
              "sameAs": []
            },
            {
              "@type": "WebSite",
              "name": "Rugalach Dad",
              "url": "https://rugalachdad.com"
            }
          ]
        })}</script>
      </Helmet>
      )}
      {cartRestoreNotice && (
        <div className="cart-restore-banner">{cartRestoreNotice}</div>
      )}
      <div className={`site-nav${showStickyNav ? '' : ' site-nav--hidden'}`}>
        <span className="site-nav__logo" onClick={() => setUiMode('storefront')}>
          <img src="https://storage.googleapis.com/rugelach-images/branding/logo-icon.png" alt="Rugalach Dad" className="site-nav__logo-icon" /> RUGALACH DAD
        </span>
        <div className="site-nav__links">
          <button onClick={() => {
            if (uiMode === 'order-success') {
              resetOrder();
            } else {
              setUiMode('storefront');
              setTimeout(() => document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' }), 0);
            }
          }} className="btn-nav">Menu</button>
          <button onClick={() => setUiMode('about')} className="btn-nav">Our Story</button>
          <button onClick={() => setShowContact(true)} className="btn-nav">Contact Us</button>
          {/* Cart icon — toggles the cart drawer. Checkout navigation now lives
              on the drawer's own button (see CartDrawer). */}
          <div className="cart-btn-wrap">
            <div
              onClick={() => setCartDrawerOpen(o => !o)}
              className="cart-btn cart-btn--active">
              <span className="cart-icon">🛒 ({totalItems})</span>
            </div>
            {cartToast && (
              <div className="cart-toast">
                ✓ {cartToast.quantity} {sizeConfig[cartToast.size]?.label} {cartToast.name} added
              </div>
            )}
          </div>
        </div>
      </div>

      <CartDrawer
        isOpen={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        cart={cart}
        sizeConfig={sizeConfig}
        wouldExceedCapacity={wouldExceedCapacity}
        onIncrement={incrementCartItem}
        onDecrement={decrementCartItem}
        onRemove={removeCartItem}
        totalCartValue={totalCartValue}
        couponApplied={couponApplied}
        couponDiscount={couponDiscount}
        finalOrderTotal={finalOrderTotal}
        totalItems={totalItems}
        fulfillmentLocked={fulfillmentLocked}
        onCheckout={() => { setCartDrawerOpen(false); handleOrderNowClick(); }}
        onBrowseMenu={() => { setCartDrawerOpen(false); scrollToMenu(); }}
      />

      {uiMode === 'storefront' && <header className="storefront-header">
		<div className="header-spacer" />
        <div className="storefront-hero">
          <div className="storefront-title-block" onClick={() => setUiMode('storefront')}>
            <img src="https://storage.googleapis.com/rugelach-images/branding/logo-icon.png" alt="Rugalach Dad" className="storefront-title__icon" />
            <div className="storefront-title__text">
              <h1 className="storefront-title">Rugalach Dad</h1>
              <p className="storefront-title__subheading">Bakery Shop</p>
            </div>
          </div>
          <p className="storefront-tagline">Flaky layers. Generous fillings. Baked fresh to order.</p>
        </div>

        {fulfillmentLocked && (
          <div className="fulfillment-banner">
            <div className="fulfillment-banner__pill">
              <span>{fulfillmentType === 'delivery' ? '🚗 Local Delivery to:' : '📦 Shipping to:'} {address}</span>
              <button onClick={clearFulfillmentLock} className="btn-text-danger">Change</button>
            </div>
          </div>
        )}
      </header>}

      {/* HERO CAROUSEL */}
      {uiMode === 'storefront' && !loading && products.length > 0 && (
        <section className="hero-carousel">
          <div className="hero-carousel__track">
            {products.filter(p => p.isNew).map((item, index) => (
			  <div key={item.id} className={`hero-carousel__slide${index === currentSlide ? ' hero-carousel__slide--active' : ''}`}>
				{item.isNew && (
				  <span className="badge-new--hero">New</span>
				)}
				<div className="hero-carousel__content">
				  <span className="hero-carousel__eyebrow">Dad's Featured Batch</span>
				  <h2 className="hero-carousel__heading">{item.name}</h2>
				  <p className="hero-carousel__desc">{item.description}</p>
				  <div className="hero-carousel__footer">
					<span className="hero-carousel__price">
					  {formatFromPrice(item) ?? `$${item.price.toFixed(2)}`}
					</span>
					<button onClick={() => openCustomizeModal(item)} className="btn-primary-sm">
					  Add
					</button>
				  </div>
				</div>
				<div className="hero-carousel__img"
				  style={hasProductImage(item) ? { backgroundImage: `url(${item.imageUrl})` } : undefined}>
				  {!hasProductImage(item) && <NoImagePlaceholder enhanced={welcomeCampaignEnabled} />}
				</div>
			  </div>
			))}
            {products.filter(p => p.isNew).length > 1 && (
              <>
                <button onClick={goToPrevSlide} className="hero-carousel__arrow hero-carousel__arrow--prev" aria-label="Previous slide">‹</button>
                <button onClick={goToNextSlide} className="hero-carousel__arrow hero-carousel__arrow--next" aria-label="Next slide">›</button>
              </>
            )}
          </div>
          <div className="hero-carousel__dots">
            {products.filter(p => p.isNew).map((_, idx) => (
              <button key={idx} onClick={() => setCurrentSlide(idx)}
                className={`carousel-dot${idx === currentSlide ? ' carousel-dot--active' : ''}`} />
            ))}
          </div>
        </section>
      )}

      {/* MENU SECTION */}
      {uiMode === 'storefront' && <section id="menu-section" className="menu-section">
        <h2 className="menu-title">
          Today's Fresh Counter Menu
        </h2>
        {loading ? (
          <p className="menu-loading-text">Gathering today's fresh ingredients...</p>
        ) : loadError ? (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <img src="https://storage.googleapis.com/rugelach-images/branding/logo-full.png" alt="Rugalach Dad" style={{ height: '64px', marginBottom: '24px' }} />
            <h2 style={{ fontFamily: 'Georgia, serif', color: '#2C1A14', fontSize: '24px', marginBottom: '16px' }}>The Oven's Still Warming Up</h2>
            <p style={{ color: '#6B4C3B', fontSize: '17px', maxWidth: '420px', lineHeight: '1.6', margin: '0 auto' }}>
              The dough is proofing, the oven is preheating, but Rugalach Dad's kitchen isn't quite ready to take your order yet. Check back in a few minutes — good things are worth the wait.
            </p>
            <div style={{ marginTop: '32px', width: '48px', height: '3px', backgroundColor: '#D4A853', borderRadius: '2px', margin: '32px auto 0' }} />
          </div>
        ) : (
          <>
            {/* Category Filter Tabs */}
            <div className="category-filters">
              <button
                onClick={() => setSelectedCategoryFilter('All')}
                className={`filter-tab${selectedCategoryFilter === 'All' ? ' filter-tab--active' : ''}`}>
                All
              </button>
              {PRODUCT_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategoryFilter(cat)}
                  className={`filter-tab${selectedCategoryFilter === cat ? ' filter-tab--active' : ''}`}>
                  {cat}
                </button>
              ))}
            </div>

            {/* Grouped Products */}
            {PRODUCT_CATEGORIES
              .map(cat => ({
                category: cat,
                items: products.filter(p => (p.category || 'Rugalach') === cat)
              }))
              .filter(group => group.items.length > 0 && (selectedCategoryFilter === 'All' || selectedCategoryFilter === group.category))
              .map(group => (
                <div key={group.category} className="product-category-group">
                  {/* Section Divider */}
                  <div className="category-divider">
                    <span className="tag-section-label">
                      {group.category}
                    </span>
                  </div>

                  {/* Products Grid */}
                  <div className="product-grid">
                    {group.items.map((product) => (
                      <div key={product.id} className="product-card">
                        {product.isNew && (
                          <span className="badge-new">New</span>
                        )}
                        <div className="product-card__img-wrap">
                          {hasProductImage(product) ? (
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE_URL; }}
                            />
                          ) : (
                            <NoImagePlaceholder enhanced={welcomeCampaignEnabled} />
                          )}
                        </div>
                        <div className="product-card__body">
                          <h3 className="product-card__name">{product.name}</h3>
                          <p className="product-card__desc">{product.description}</p>
                          <div className="product-card__footer">
                            <span className="product-card__price">
                              {formatFromPrice(product) ?? `$${product.price ? product.price.toFixed(2) : '0.00'}`}
                            </span>
                            <button onClick={() => openCustomizeModal(product)} className="btn-primary-sm">
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </>
        )}
      </section>}

      {/* CUSTOMIZATION MODAL */}
      {customizeProduct && (() => {
		  const activeSizeConfig = getActiveSizeConfig(customizeProduct);
		  return (
			<div className="modal-overlay">
			  <div className="modal-body modal-body--customize">
				<button onClick={closeModal} className="btn-close">✕</button>
				<h2 className="modal-title">{customizeProduct.name}</h2>
				<p className="modal-desc">{customizeProduct.description}</p>

				<label className="label-caps">Select Size</label>
				<div className="customize-size-grid">
				  {Object.entries(activeSizeConfig).map(([size, cfg]) => (
					<div key={size} onClick={() => { setSelectedSize(size); setSelectedQuantity(cfg.step); setAddToCartError(null); }}
					  className={`size-tile${selectedSize === size ? ' size-tile--active' : ''}`}>
					  <div className="size-tile__label">{cfg.label}</div>
					  <div className="size-tile__price">${calcUnitPrice(customizeProduct, size)?.toFixed(2) ?? '—'}/pc</div>
					</div>
				  ))}
				</div>

				<label className="label-caps">Quantity</label>
				<div className="customize-qty-row">
				  <button onClick={() => setSelectedQuantity(q => Math.max(activeSizeConfig[selectedSize].step, q - activeSizeConfig[selectedSize].step))}
					className="btn-qty btn-qty--lg">−</button>
				  <span className="qty-display">{selectedQuantity}</span>
				  <button onClick={() => setSelectedQuantity(q => q + activeSizeConfig[selectedSize].step)}
					disabled={wouldExceedCapacity(selectedSize, activeSizeConfig[selectedSize].step)}
					className="btn-qty btn-qty--lg">+</button>
				  <span className="qty-unit">pieces</span>
				  {wouldExceedCapacity(selectedSize, activeSizeConfig[selectedSize].step) && (
					  <div className="qty-capacity-warn">
						Sorry, we've reached the maximum we can make in a day.
						If you need more than this amount, please contact us and we will review and get back to you.
					  </div>
					)}
				</div>
				
				{addToCartError && (
				  <div className="add-to-cart-error">{addToCartError}</div>
				)}

				{customizations.productCustomizations?.length > 0 && (
				  <>
					<label className="label-caps">Product Customizations</label>
					<div className="addon-rows">
					  {customizations.productCustomizations.map(addon => (
						<div key={addon.id} onClick={() => toggleAddon(addon)}
						  className={`addon-row${selectedAddons.find(a => a.id === addon.id) ? ' addon-row--selected' : ''}`}>
						  <div className="addon-row__inner">
							<span className="addon-name">{addon.name}</span>
							{addon.description && addon.description.trim() !== "" && (
							  <div onClick={(e) => e.stopPropagation()} onMouseEnter={() => setHoveredAddonId(addon.id)} onMouseLeave={() => setHoveredAddonId(null)} className="addon-info-wrap">
								<span className="addon-info-icon">?</span>
								<span className={`addon-tooltip${hoveredAddonId === addon.id ? ' addon-tooltip--visible' : ''}`}>
								  <span dangerouslySetInnerHTML={{ __html: addon.description }} />
								</span>
							  </div>
							)}
						  </div>
						  <span className={`addon-price${addon.priceDelta > 0 ? ' addon-price--positive' : ''}`}>
							{addon.priceDelta > 0 ? `+$${addon.priceDelta.toFixed(2)}/pc` : 'Free'}
						  </span>
						</div>
					  ))}
					</div>
				  </>
				)}

				{customizations.toppings?.length > 0 && (
				  <>
					<label className="label-caps">Toppings</label>
					<div className="addon-rows">
					  {customizations.toppings.map(addon => (
						<div key={addon.id} onClick={() => toggleAddon(addon, true)}
						  className={`addon-row${selectedAddons.find(a => a.id === addon.id) ? ' addon-row--selected' : ''}`}>
						  <span className="addon-name">{addon.name}</span>
						  <span className={`addon-price${addon.priceDelta > 0 ? ' addon-price--positive' : ''}`}>
							{addon.priceDelta > 0 ? `+$${addon.priceDelta.toFixed(2)}/pc` : 'Free'}
						  </span>
						</div>
					  ))}
					</div>
				  </>
				)}

				<div className="line-total-box">
				  <span className="line-total__label">{selectedQuantity} × {activeSizeConfig[selectedSize].label}</span>
				  <span className="line-total__amount">${calcLineTotal().toFixed(2)}</span>
				</div>

				<button onClick={addToCartFromModal} className="btn-primary">
				  Add to Order — ${calcLineTotal().toFixed(2)}
				</button>
			  </div>
			</div>
		  );
		})()}

      {/* STEP 1: ADDRESS ENTRY */}
      {uiMode === 'order' && (
        <section className="checkout-screen" style={{ maxWidth: '500px' }}>
          <button onClick={() => setUiMode('storefront')} className="btn-back">← Back to Menu</button>
          <h2 className="checkout-heading">📍 Where are we sending these?</h2>
          <p className="checkout-desc">Enter your address and we'll show you the available delivery options.</p>
          <form onSubmit={handleFormSubmit} className="form-group">
            <div>
              <label className="label-caps-md">Street Address</label>
              <AddressAutocomplete value={address} onChange={(val) => { setAddress(val); setValidationResult(null); }} placeholder="123 Main St, Alpharetta, GA, USA" />
            </div>
            {validating && <p className="validating-msg">📍 Checking distance from Alpharetta...</p>}
            {validationResult && !validationResult.valid && (
              <div className="validation-error">{validationResult.message}</div>
            )}
            <button type="submit" disabled={validating || !address.trim()} className="btn-primary">
              {validating ? 'Checking...' : 'Continue →'}
            </button>
          </form>
        </section>
      )}

      {/* STEP 2: CHOOSE FULFILLMENT */}
      {uiMode === 'choose-fulfillment' && (
        <section className="checkout-screen checkout-screen--centered" style={{ maxWidth: '600px' }}>
          <button onClick={() => { setUiMode('order'); setValidationResult(null); }} className="checkout-back-float">← Back</button>
          <div className="checkout-clearfix" />
          <h2 className="checkout-heading--mt">How would you like your Rugalach?</h2>
          <p className="checkout-address-note">Delivering to: <strong>{address}</strong></p>
          <div className={`fulfillment-grid${validationResult?.valid ? ' fulfillment-grid--two-col' : ''}`}>
            {validationResult?.valid && (
              <div onClick={() => !localDeliveryDisabled && handleFulfillmentChoice('delivery')}
                className={`fulfillment-card${!localDeliveryDisabled ? '' : ' fulfillment-card--disabled'}`}>
                <div className="fulfillment-card__icon">🚗</div>
                <h3 className="fulfillment-card__title">Local Delivery</h3>
                <p className="fulfillment-card__desc">Brought fresh right to your doorstep.</p>
                {localDeliveryDisabled ? (
                  <div className="fulfillment-min-warn">Local delivery is currently unavailable.</div>
                ) : (
                  <>
                    <span className="fulfillment-card__badge">
                      Min. order: ${localDeliveryMinimum.toFixed(2)}
                    </span>
                    {totalCartValue < localDeliveryMinimum && (
                      <div className="fulfillment-min-note">
                        Add ${(localDeliveryMinimum - totalCartValue).toFixed(2)} more to qualify for local delivery. You can adjust item quantities on the Review Your Order screen.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {/* Shipping card with fee note */}
            <div onClick={() => !shippingDisabled && handleFulfillmentChoice('shipping')}
              className={`fulfillment-card${shippingDisabled ? ' fulfillment-card--disabled' : ''}`}>
              <div className="fulfillment-card__icon">📦</div>
              <h3 className="fulfillment-card__title">Nationwide Shipping</h3>
              <p className="fulfillment-card__desc">Freshly sealed and shipped to you.</p>
              {shippingDisabled ? (
                <div className="fulfillment-min-warn">Nationwide shipping is currently unavailable.</div>
              ) : (
                <span className="fulfillment-card__badge">+${shippingFeeAmount.toFixed(2)} shipping fee</span>
              )}
            </div>
          </div>
          {!validationResult?.valid && (
            <p className="fulfillment-zone-note">Your address is outside our local delivery zone (25 mi from Alpharetta, GA).</p>
          )}
        </section>
      )}

      {/* DATE PICKER SCREEN */}
      {uiMode === 'date-picker' && (
        <section className="checkout-screen" style={{ maxWidth: '560px' }}>
          <button onClick={() => setUiMode('choose-fulfillment')} className="btn-back">← Back</button>
          <h2 className="checkout-heading">📅 Pick a Date</h2>
          <p className="checkout-desc">
            {standbyModeEnabled
              ? "Choose when you'd like your order ready. We're currently only taking weekend orders — grayed out dates are unavailable."
              : "Choose when you'd like your order ready. Grayed out dates are fully booked."}
          </p>
          <div className="cart-summary-box">
            <div className="cart-summary-label">Your Order</div>
            
			{cart.map(item => {
			  const step = sizeConfig[item.size]?.step || 1;
			  
			  // Check if adding one more of THIS item would go over capacity
			  const itemIsBlocked = wouldExceedCapacity(item.size, step);

			  return (
				<CartLineItem 
				  key={item.id} 
				  item={item} 
				  onIncrement={incrementCartItem} 
				  onDecrement={decrementCartItem} 
				  onRemove={removeCartItem} 
				  // This will properly gray out the button when it's blocked!
				  atCapacity={itemIsBlocked} 
				/>
			  );
			})}

			{/* 
			  Show the warning if ANY item in the cart is currently 
			  blocked from adding more due to the capacity limit.
			*/}
			{cart.some(item => wouldExceedCapacity(item.size, sizeConfig[item.size]?.step || 1)) && (
			  <div className="cart-capacity-warn">
				You've reached the maximum we can make in a day. If you need more than this amount, please <ContactUsLink onClick={() => setShowContact(true)} /> and we will review and get back to you.
			  </div>
			)}

            <div className="cart-subtotal-row">
              <span>Subtotal</span><span>${totalCartValue.toFixed(2)}</span>
            </div>
          </div>
          <div className="date-grid">
            {availableDates.map(d => {
              const dateObj = new Date(d.date + 'T00:00:00');
              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = dateObj.getDate();
              const isSelected = selectedDate === d.date;
              // Standby Mode: weekdays (Mon-Fri) are greyed out and unselectable.
              // getDay() 0 = Sunday, 6 = Saturday.
              const dayOfWeek = dateObj.getDay();
              const isStandbyWeekday = standbyModeEnabled && dayOfWeek !== 0 && dayOfWeek !== 6;
              const isUnavailable = !d.available || isStandbyWeekday;
              return (
                <div key={d.date} onClick={() => {
                  if (isUnavailable) return;
                  setSelectedDate(d.date);
                  checkCapacityForDate(d.date);
                }}
                  className={`date-tile${isSelected ? ' date-tile--selected' : ''}${isUnavailable ? ' date-tile--unavailable' : ''}`}
                  style={{ background: tileBg(d, isSelected) }}>
                  <div className="date-tile__day">{dayName}</div>
                  <div className="date-tile__num">{dayNum}</div>
                </div>
              );
            })}
          </div>
          {selectedDate && (
            <p className="date-selected-info">
              ✅ Selected: {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          )}
          {checkingCapacity && (
            <p className="date-checking-info">Checking availability...</p>
          )}
          {!checkingCapacity && capacityError && (
            <div className="alert-error">
              {standbyModeEnabled ? (
                <>
                  <div className="alert-error__title">⚠️ We can't bake this much right now</div>
                  <div className="alert-error__body">
                    Sorry, at the moment we can't bake all the items in your cart. Please reduce your quantities, or <ContactUsLink onClick={() => setShowContact(true)} /> to ask about a special order.
                  </div>
                </>
              ) : (
                <>
                  <div className="alert-error__title">⚠️ Not enough capacity for this date</div>
                  <div className="alert-error__body">
                    This date doesn't have enough capacity for your order.
                  </div>
                  {capacityError.nextAvailableDate && (
                    <button
                      onClick={() => {
                        setSelectedDate(capacityError.nextAvailableDate);
                        checkCapacityForDate(capacityError.nextAvailableDate);
                      }}
                      className="btn-next-date">
                      Move to {new Date(capacityError.nextAvailableDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} →
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          <button
            disabled={!selectedDate || !!capacityError || checkingCapacity}
            onClick={() => setUiMode('customer-info')}
            className="btn-primary">
            {checkingCapacity ? 'Checking...' : 'Next: Your Info →'}
          </button>
        </section>
      )}

      {/* CUSTOMER INFO SCREEN */}
      {uiMode === 'customer-info' && (
        <section className="checkout-screen" style={{ maxWidth: '500px' }}>
          <button onClick={() => setUiMode('date-picker')} className="btn-back">← Back</button>
          <h2 className="checkout-heading">👤 Your Details</h2>
          <p className="checkout-desc">So Dad knows who to bake for.</p>
          <div className="form-group">
            <div>
              <label className="label-caps-md">Full Name *</label>
              <input type="text" required value={customerInfo.name} onChange={e => setCustomerInfo(p => ({...p, name: e.target.value}))} placeholder="Jane Smith"
                className="form-input" />
            </div>
            <div>
              <label className="label-caps-md">Email Address *</label>
              <input type="email" required value={customerInfo.email} onChange={e => setCustomerInfo(p => ({...p, email: e.target.value}))} placeholder="jane@email.com"
                className="form-input" />
            </div>
            <div>
              <label className="label-caps-md">Phone Number</label>
              <input type="tel" value={customerInfo.phone} onChange={e => setCustomerInfo(p => ({...p, phone: e.target.value}))} placeholder="555-123-4567"
                className="form-input" />
            </div>
            <div>
              <label className="label-caps-md">Comments</label>
              <textarea
                rows="3"
                value={customerInfo.comments}
                onChange={e => setCustomerInfo(p => ({ ...p, comments: e.target.value }))}
                placeholder="Allergies, special requests, delivery instructions..."
                className="form-textarea"
              />
            </div>
            <div className="toggle-row">
              <label className="toggle-label">
                Send me updates about new products and customizations
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={newsletterOptIn}
                onClick={() => setNewsletterOptIn(v => !v)}
                className={`toggle${newsletterOptIn ? ' toggle--on' : ''}`}
              >
                <span className="toggle-thumb" />
              </button>
            </div>
            <button onClick={() => setUiMode('review')} disabled={!customerInfo.name || !customerInfo.email}
              className="btn-primary">
              Next: Review Order →
            </button>
          </div>
        </section>
      )}

      {/* REVIEW & PLACE ORDER SCREEN */}
      {uiMode === 'review' && (
        <section className="checkout-screen" style={{ maxWidth: '520px' }}>
          <button onClick={() => setUiMode('customer-info')} className="btn-back">← Back</button>
          <h2 className="checkout-heading--review">🧾 Review Your Order</h2>
          {!checkingCapacity && capacityError && (
            <div className="alert-error">
              {standbyModeEnabled ? (
                <>
                  <div className="alert-error__title">⚠️ We can't bake this much right now</div>
                  <div className="alert-error__body">
                    Sorry, at the moment we can't bake all the items in your cart. Please reduce your quantities, or <ContactUsLink onClick={() => setShowContact(true)} /> to ask about a special order.
                  </div>
                </>
              ) : (
                <>
                  <div className="alert-error__title">⚠️ Not enough capacity for this date</div>
                  <div className="alert-error__body">
                    This date doesn't have enough capacity for your order.
                  </div>
                  {capacityError.nextAvailableDate && (
                    <button
                      onClick={() => {
                        setSelectedDate(capacityError.nextAvailableDate);
                        checkCapacityForDate(capacityError.nextAvailableDate);
                      }}
                      className="btn-next-date">
                      Move to {new Date(capacityError.nextAvailableDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} →
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          <div className="summary-box">
            <div className="summary-section-label">Order Items</div>
            			
			{cart.map(item => {
			  const step = sizeConfig[item.size]?.step || 1;
			  
			  // Check if adding one more of THIS item would go over capacity
			  const itemIsBlocked = wouldExceedCapacity(item.size, step);

			  return (
				<CartLineItem 
				  key={item.id} 
				  item={item} 
				  onIncrement={incrementCartItem} 
				  onDecrement={decrementCartItem} 
				  onRemove={removeCartItem} 
				  // This will properly gray out the button when it's blocked!
				  atCapacity={itemIsBlocked} 
				/>
			  );
			})}

			{/* 
			  Show the warning if ANY item in the cart is currently 
			  blocked from adding more due to the capacity limit.
			*/}
			{cart.some(item => wouldExceedCapacity(item.size, sizeConfig[item.size]?.step || 1)) && (
			  <div className="cart-capacity-warn">
				You've reached the maximum we can make in a day. If you need more than this amount, please <ContactUsLink onClick={() => setShowContact(true)} /> and we will review and get back to you.
			  </div>
			)}

            <div className="cart-subtotal-row">
              <span>Subtotal</span><span>${totalCartValue.toFixed(2)}</span>
            </div>

            {/* Shipping fee line item */}
            {shippingFee > 0 && (
              <div className="shipping-fee-row">
                <span>📦 Shipping fee</span>
                <span className="shipping-fee-amount">+${shippingFee.toFixed(2)}</span>
              </div>
            )}

            {/* Coupon code entry */}
            <div className="coupon-row">
              <input
                type="text"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value)}
                placeholder="Coupon code"
                className="coupon-input"
              />
              <button
                type="button"
                onClick={applyCoupon}
                disabled={couponLoading || !couponCode.trim()}
                className="coupon-apply-btn">
                {couponLoading ? 'Applying...' : 'Apply'}
              </button>
            </div>
            {couponError && <div className="coupon-error">{couponError}</div>}
            {couponApplied && (
              <div className="coupon-applied-row">
                <span>✓ {couponApplied}: −${couponDiscount.toFixed(2)}</span>
                <button type="button" onClick={removeCoupon} className="coupon-remove-btn">Remove</button>
              </div>
            )}
            {couponApplied && (
              <div className="coupon-discount-row">
                <span>{couponApplied}</span><span>−${couponDiscount.toFixed(2)}</span>
              </div>
            )}

            <div className="total-row">
              <span>Total</span><span>${finalOrderTotal.toFixed(2)}</span>
            </div>
          </div>
          <div className="order-detail-box">
            <div className="order-detail-label">Details</div>
            <div>📅 {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
            <div>{fulfillmentType === 'delivery' ? '🚗' : '📦'} {fulfillmentType === 'delivery' ? 'Delivery to' : 'Shipping to'}: {address || 'Pickup'}</div>
            <div>👤 {customerInfo.name} · {customerInfo.email}</div>
            {customerInfo.comments && (
              <div>💬 {customerInfo.comments}</div>
            )}
			{newsletterOptIn && <div className="subscribed-note">📧 Subscribed to product updates</div>}
          </div>

          <button onClick={handlePlaceOrder} disabled={orderSubmitting || belowMinimumForDelivery}
            className="btn-primary">
            {orderSubmitting ? 'Placing Order...' : 'Continue to Payment →'}
          </button>
          {belowMinimumForDelivery && (
            <div className="delivery-min-warn">
              <div className="delivery-min-warn__text">
                For local delivery the minimum order is ${localDeliveryMinimum.toFixed(2)}. Add ${(localDeliveryMinimum - totalCartValue).toFixed(2)} more.
              </div>
            </div>
          )}
          <p className="review-note">You will be able to select a payment method next.</p>
        </section>
      )}
	  
	  {/* PAYMENT SCREEN */}
	  {uiMode === 'payment' && (
        <div className="payment-screen">
          <h2 className="payment-screen__title">Choose Payment Method</h2>

          {surpriseGift && (
            <div className="surprise-gift-reveal">
              <div className="surprise-gift-reveal__heading">🎉 Surprise! You unlocked a gift</div>
              <span className="surprise-gift-reveal__badge">
                🎁 {surpriseGift.productName}: −${surpriseGift.discountAmount.toFixed(2)}
              </span>
              <p className="surprise-gift-reveal__note">Your total below already reflects this discount.</p>
            </div>
          )}

          {/* Order Summary */}
          <div className="payment-summary">
            <h3 className="payment-summary__title">Order Details</h3>
            <div className="payment-summary__detail">
              <div>{fulfillmentType === 'delivery' ? '🚗' : '📦'} {fulfillmentType === 'delivery' ? 'Delivery to' : 'Shipping to'}: {address}</div>
              <div>📦 Pickup date: {selectedDate}</div>
              <div className="payment-summary__total">Total: ${finalOrderTotal.toFixed(2)}</div>
            </div>
          </div>

          {/* Card Payment Option */}
  		  <div className="payment-option-wrap">
			<div
				onClick={() => {
					setPaymentMethod('CARD');
					if (!stripeClientSecret) initStripePayment();
				}}
				className={`payment-option${paymentMethod === 'CARD' ? ' payment-option--selected-card' : ''}`}
			>
				<input
					type="radio"
					name="payment"
					value="CARD"
					checked={paymentMethod === 'CARD'}
					onChange={() => {}}
					className="payment-radio"
				/>
				<label className="payment-option__label">
					💳 Credit / Debit Card, Apple Pay, or Google Pay
				</label>
				<p className="payment-option__desc">
					Secure payment powered by Stripe. No account required.
				</p>
			</div>

			{paymentMethod === 'CARD' && paymentStep === 'processing' && !stripeClientSecret && (
				<p className="stripe-init-msg">Initializing payment...</p>
			)}

			{paymentMethod === 'CARD' && stripeClientSecret && stripePromise && (
				<div className="stripe-wrap">
					<Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
						<StripeCheckoutForm
							orderTotal={finalOrderTotal}
							onSuccess={() => { setPaymentStep('paid'); setUiMode('order-success'); }}
							onError={(msg) => { setPaymentError(msg); setPaymentStep('error'); }}
						/>
					</Elements>
				</div>
			)}

			{paymentStep === 'error' && paymentMethod === 'CARD' && (
				<div className="payment-error" style={{ marginLeft: '24px' }}>
					{paymentError}
					<button
						onClick={() => { setPaymentStep('select'); setStripeClientSecret(null); setStripePromise(null); }}
						className="payment-error__retry">
						Try Again
					</button>
				</div>
			)}
		  </div>

          {/* Cash Option — only if fulfillmentType === 'delivery' */}
          {fulfillmentType === 'delivery' && (
            <div className="payment-option-wrap">
              <div
                onClick={() => { setPaymentMethod('CASH'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`payment-option${paymentMethod === 'CASH' ? ' payment-option--selected-cash' : ''}`}
              >
                <input
                  type="radio"
                  name="payment"
                  value="CASH"
                  checked={paymentMethod === 'CASH'}
                  onChange={(e) => { setPaymentMethod(e.target.value); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="payment-radio"
                />
                <label className="payment-option__label payment-option__label--cash">
                  💵 Pay with Cash at Delivery
                </label>
                <div className="cash-amount-warn">
                  ⚠️ Please have exact amount ready: ${finalOrderTotal.toFixed(2)}
                </div>
              </div>

              {paymentMethod === 'CASH' && (
                <div className="cash-confirm-wrap">
                  {paymentStep === 'select' && (
                    <button onClick={handleCashPayment} className="cash-confirm-btn">
                      Confirm Cash Payment
                    </button>
                  )}
                  {paymentStep === 'processing' && <p className="cash-confirm-processing">Confirming...</p>}
                </div>
              )}
            </div>
          )}

          {/* Venmo Option — available for all fulfillment types */}
          <div className="payment-option-wrap">
            <div
              onClick={() => { setPaymentMethod('VENMO'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`payment-option${paymentMethod === 'VENMO' ? ' payment-option--selected-venmo' : ''}`}
            >
              <input
                type="radio"
                name="payment"
                value="VENMO"
                checked={paymentMethod === 'VENMO'}
                onChange={(e) => { setPaymentMethod(e.target.value); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="payment-radio"
              />
              <label className="payment-option__label payment-option__label--venmo">
                💸 Pay with Venmo
              </label>
              {paymentMethod === 'VENMO' && (
                <div className="venmo-details">
                  <div className="venmo-details__row">
                    Send payment to: <span className="venmo-details__handle">{venmoHandle}</span>
                  </div>
                  <div className="venmo-details__row">
                    Amount to send: <span className="venmo-details__amount">${finalOrderTotal.toFixed(2)}</span>
                  </div>
                  <p className="venmo-details__note">
                    Send payment via Venmo, then click confirm. Your order will be processed once payment is verified.
                  </p>
                </div>
              )}
            </div>

            {paymentMethod === 'VENMO' && (
              <div className="venmo-confirm-wrap">
                {paymentStep === 'select' && (
                  <button onClick={handleVenmoPayment} className="venmo-confirm-btn">
                    Confirm Venmo Payment
                  </button>
                )}
                {paymentStep === 'processing' && <p className="venmo-confirm-processing">Confirming...</p>}
              </div>
            )}
          </div>

          {/* Error message (if any) */}
          {paymentError && (
            <div className="payment-error-banner">
              {paymentError}
            </div>
          )}
        </div>
      )}

      {/* ORDER SUCCESS SCREEN */}
      {uiMode === 'order-success' && (
        <section className="order-success">
          <div className="order-success__emoji">🥐</div>
          <h2 className="order-success__heading">✓ Order Confirmed</h2>
          <p className="order-success__message">
            {paymentMethod === 'CASH'
              ? `Your order is confirmed. Please have $${finalOrderTotal.toFixed(2)} ready for your delivery.`
              : paymentMethod === 'VENMO'
              ? `Thanks! Once we verify your Venmo payment of $${finalOrderTotal.toFixed(2)} to ${venmoHandle}, your order will be processed.`
              : paymentMethod === 'COUPON'
              ? 'Paid in full with coupon! Your order is confirmed.'
              : 'Thank you for your payment! Your order is confirmed.'}
          </p>
          {couponApplied && couponDiscount > 0 && (
            <div className="order-success__coupon-row">
              <span>{couponApplied.startsWith('FRESH5_') ? '🎁 Surprise Gift Discount' : couponApplied}</span><span>−${couponDiscount.toFixed(2)}</span>
            </div>
          )}
          <p className="order-success__email-note">
            Confirmation details have been sent to {customerInfo.email}.
          </p>
          <button onClick={resetOrder}
            className="btn-primary" style={{ padding: '12px 40px', width: 'auto' }}>
            Back to Menu
          </button>
        </section>
      )}

      {/* CONTACT MODAL */}
      {showContact && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: '480px' }}>
            <button onClick={closeContact} className="btn-close">✕</button>
            {contactSuccess ? (
              <div className="contact-success">
                <div className="contact-success__emoji">✅</div>
                <h2 className="contact-success__heading">Message Sent!</h2>
                <p className="contact-success__desc">Dad will get back to you soon.</p>
                <button onClick={closeContact} className="btn-primary" style={{ width: 'auto', padding: '10px 30px' }}>Close</button>
              </div>
            ) : (
              <>
                <h2 className="contact-modal__heading">📬 Contact Us</h2>
                <p className="contact-modal__desc">Have a question or special request? We'd love to hear from you.</p>
                <form onSubmit={handleContactSubmit} className="form-group-sm">
                  <div>
                    <label className="label-caps">Full Name *</label>
                    <input type="text" required value={contactForm.name} onChange={e => setContactForm(p => ({...p, name: e.target.value}))}
                      placeholder="Jane Smith"
                      className="form-input-sm" />
                  </div>
                  <div>
                    <label className="label-caps">Email *</label>
                    <input type="email" required value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))}
                      placeholder="jane@email.com"
                      className="form-input-sm" />
                  </div>
                  <div>
                    <label className="label-caps">Phone</label>
                    <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => ({...p, phone: e.target.value}))}
                      placeholder="555-123-4567"
                      className="form-input-sm" />
                  </div>
                  <div>
                    <label className="label-caps">Message *</label>
                    <textarea required rows="4" value={contactForm.message} onChange={e => setContactForm(p => ({...p, message: e.target.value}))}
                      placeholder="Tell us what's on your mind..."
                      className="form-textarea-sm" />
                  </div>
                  <button type="submit" disabled={contactSubmitting} className="btn-primary" style={{ marginTop: '4px' }}>
                    {contactSubmitting ? 'Sending...' : 'Send Message 📬'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* OUR STORY SCREEN */}

      {/* WELCOME POPUP */}
      {showWelcomePopup && (
        <div className="welcome-popup-overlay">
          <div className="welcome-popup-card">
            <WelcomeFireworks />
            <div className="welcome-popup__content">
              <div className="welcome-popup__row1">
                <span className="welcome-popup__emoji-lg">🎉</span>
                <h2 className="welcome-popup__heading">We're Open!</h2>
              </div>
              <p className="welcome-popup__subheading"><em>The Rugalach Dad is officially baking.</em></p>
              <p className="welcome-popup__body">Handmade rugalach, fresh from our oven to your door. Come explore what's baking today.</p>

              <div className="welcome-popup__notes">
                <p className="welcome-popup__note">
                  🎁 <strong>A little surprise:</strong> See a product that's “still in the oven” with no photo yet? Be the first to order it and you'll unwrap a special gift at checkout. (Only one gift discount applies per order.)
                </p>
                <p className="welcome-popup__note">
                  🛠️ <strong>A handmade shop:</strong> Just like our pastries, this online bakery was built by hand — <strong>by me</strong>. If you spot any bugs (the software kind, not the kitchen kind!), please let me know via <button type="button" onClick={openContactFromWelcome} className="welcome-popup__link">Contact Us</button>.
                </p>
                <p className="welcome-popup__note">
                  💬 <strong>We'd love your feedback:</strong> Whether it's about the shopping experience, our products, or anything else — your thoughts help us get better. Share them anytime via <button type="button" onClick={openContactFromWelcome} className="welcome-popup__link">Contact Us</button>.
                </p>
              </div>

              <div className="welcome-popup__divider" />

              <label className="welcome-popup__checkbox-row">
                <input type="checkbox" checked={dontShowWelcomeAgain} onChange={e => setDontShowWelcomeAgain(e.target.checked)} />
                Don't show this again
              </label>

              <button onClick={dismissWelcomePopup} className="welcome-popup__dismiss-btn">
                Let's Bake! 🥐
              </button>
            </div>
          </div>
        </div>
      )}

      {uiMode === 'about' && (
		<section className="about-section">
          <button onClick={() => setUiMode('storefront')} className="btn-back">
            ← Back to Menu
          </button>
          <div className="about-card">
            {/* Header Section */}
            <div className="about-card__header">
              <div className="about-card__eyebrow">
                About the Dad
              </div>
              <h2 className="about-card__heading">
                👨‍🍳 Three daughters, one oven, endless rugalach.
              </h2>
              <p className="about-card__body">
                Food and family were the center of my life during childhood—watching and learning in my grandma's and mom's kitchens, I developed a general love for being in the kitchen that eventually turned into a lifelong passion. For years, practicing, baking, and refining recipes was just a hobby. But when a sudden shift in my high-tech career opened up a new door, I decided it was time for a positive pivot. That shift gave me the time to turn my favorite hobby into a true calling. And yes, it's "Rugalach" — not "Rugelach." That's intentional. In Hebrew, we say <em>ro-ga-lach</em>, and our recipes stay true to that Israeli tradition — flaky, buttery, and straight from the style I grew up with.
              </p>
              <p className="about-card__body--last">
                While it all started with traditional, flaky rugalach made fresh for my three girls on Saturday mornings, <strong className="about-card__highlight">Rugalach Dad</strong> has grown into something much bigger.
              </p>
            </div>

            {/* Divider */}
            <div className="about-card__divider"></div>

            {/* Story Block 1: The Perfect Roll */}
            <div className="about-card__block">
              <h3 className="about-card__subheading">
                🥐 The Perfect Roll (and Twist)
              </h3>
              <p className="about-card__body">
                We craft classic small and large rugalach, but we've also shaken things up with our signature <strong className="about-card__highlight">Rugalach Balls</strong>—a larger, braided pastry perfect for sharing (or keeping all to yourself!). {/* PLACEHOLDER: expand here with the Ball origin story — braided dough rolled into a ball, inspired by braiding my three girls' hair and the World Cup */}
              </p>
              <p className="about-card__body--last">
                Our signature flavors respect tradition while leaning into fun, creative twists. You'll always find the <strong className="about-card__highlight">Classic Chocolate</strong> that everyone fell in love with, alongside inventive creations like <strong className="about-card__highlight">PB&J</strong>, <strong className="about-card__highlight">Strawberry Cheesecake</strong>, and the indulgent <strong className="about-card__highlight">Dubai Chocolate</strong>.
              </p>
            </div>

            {/* Story Block 2: Beyond the Rugalach */}
            <div className="about-card__block">
              <h3 className="about-card__subheading">
                🍪 Beyond the Rugalach
              </h3>
              <p className="about-card__body--last">
                Don't let the name fool you—we can't stop at just one pastry! Rugalach Dad's kitchen is always humming with a variety of baked goods. We cannot focus only on rugalach, so we bake delicate specialty cookies from around the world, like melt-in-your-mouth <strong className="about-card__highlight">Alfajores</strong> and rich, layered <strong className="about-card__highlight">Baklava</strong>, right next to crowd-pleasing favorites like our classic <strong className="about-card__highlight">Chocolate Babka Cake</strong>.
              </p>
            </div>

            {/* Story Block 3: Fresh To Your Door */}
            <div className="about-card__block--last">
              <h3 className="about-card__subheading">
                📦 Fresh To Your Door
              </h3>
              <p className="about-card__body">
                Every single batch is made by hand and baked fresh to order right here in Alpharetta, GA.
              </p>
              <p className="about-card__body--last">
                We want to bring that warm, nostalgic weekend family feeling right to your table, whether you're sharing a sweet moment with loved ones or discovering a new favorite global treat. We offer <strong className="about-card__highlight">local delivery</strong> for nearby neighbors (you'll see the delivery option automatically pop up at checkout if you're in our radius!), as well as convenient shipping options if you're a bit further away.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      {(uiMode === 'storefront' || uiMode === 'about') && (
        <footer className="site-footer">
          <div className="site-footer__inner">
            <h3 className="site-footer__name">
              <img src="https://storage.googleapis.com/rugelach-images/branding/logo-icon.png" alt="Rugalach Dad" className="site-footer__logo-icon" /> RUGALACH DAD
            </h3>
            <p className="site-footer__tagline">Flaky layers. Generous fillings. Baked fresh to order.</p>
            <button onClick={() => setShowContact(true)} className="site-footer__contact-btn">
              Contact Us
            </button>
            <p className="site-footer__legal">© {new Date().getFullYear()} Rugalach Dad. All rights reserved. · Product photos are enhanced with AI tools. What arrives at your door is even better.</p>
          </div>
        </footer>
      )}
      </>
    </div>
  );
}